import React, { useState, useEffect, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LocalizationProvider } from './contexts/LocalizationContext'
import { AuthForm } from './components/Auth/AuthForm'
import { Dashboard } from './components/Dashboard/Dashboard'
import { SubscriptionGuard } from './components/Subscription/SubscriptionGuard'
import { Landing } from './pages/Landing'
import Terms from './pages/Terms'
import TermsXsem from './pages/TermsXsem'
import Privacy from './pages/Privacy'
import Refund from './pages/Refund'
import Contact from './pages/Contact'
import AnalyticsService from './services/analyticsService'

// Lazy load pour réduire le bundle initial
const SubDirectZxsem = React.lazy(() => import('./pages/SubDirectZxsem'))
const ReactivatePage = React.lazy(() => import('./pages/ReactivatePage'))

function SubscriptionEntryPoint() {
  return <SubDirectZxsem />
}

// Composant pour tracker les changements de page
function PageTracker() {
  const location = useLocation()

  useEffect(() => {
    AnalyticsService.trackPageView(location.pathname, document.title)

    // Persister le mode d'abonnement lorsqu'il est présent dans l'URL
    const params = new URLSearchParams(location.search)
    const modeFromUrl = params.get('sub') || params.get('mode')
    if (modeFromUrl === 'direct' || modeFromUrl === 'direct2') {
      sessionStorage.setItem('preferred_subscription_mode', modeFromUrl)
      localStorage.setItem('preferred_subscription_mode', modeFromUrl)
    }
  }, [location])

  return null
}

// Component for protected routes that require authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-paybyphone-light flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-paybyphone-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children}</>
}

// Auth page component
function AuthPage() {
  const { user } = useAuth()
  const location = useLocation() as any
  const [searchParams] = useSearchParams()
  const authModeParam = searchParams.get('auth') || searchParams.get('mode')
  const initialMode = authModeParam === 'signup' ? 'signup' : 'signin'
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>(initialMode)

  const modeParam = searchParams.get('sub') || searchParams.get('mode')
  const sessionMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const directModeFromSession = sessionMode === 'direct' || sessionMode === 'direct2'
  const directModeParam =
    modeParam === 'direct' || modeParam === 'direct2'
      ? modeParam
      : directModeFromSession
        ? sessionMode
        : null

  const stateRedirect = location?.state?.from as string | undefined
  const forceSubscriptionRedirect = directModeParam === 'direct'
  const redirectTo = directModeParam === 'direct2'
    ? '/dashboard'
    : forceSubscriptionRedirect
      ? '/subscription'
      : (stateRedirect || '/dashboard')

  const resolvedRedirectTo = React.useMemo(() => {
    const isSubscriptionTarget = redirectTo.startsWith('/subscription')

    if (!isSubscriptionTarget || !directModeParam) return redirectTo

    const [path, queryString = ''] = redirectTo.split('?')
    const params = new URLSearchParams(queryString)

    if (directModeParam && !params.has('sub')) {
      params.set('sub', directModeParam)
    }

    const nextQuery = params.toString()
    return nextQuery ? `${path}?${nextQuery}` : path
  }, [redirectTo, directModeParam])

  // If user is already logged in, redirect to the page they originally requested
  if (user) {
    return <Navigate to={resolvedRedirectTo} replace />
  }

  return (
    <AuthForm
      mode={authMode}
      onToggleMode={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')}
    />
  )
}

function App() {
  useEffect(() => {
    AnalyticsService.init()
  }, [])

  return (
    <LocalizationProvider>
      <AuthProvider>
        <Router>
          <PageTracker />
          <Suspense fallback={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          }>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/terms-xsem" element={<TermsXsem />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reactivate" element={
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div></div>}>
                  <ReactivatePage />
                </Suspense>
              } />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <SubscriptionGuard>
                      <Dashboard />
                    </SubscriptionGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <SubscriptionEntryPoint />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subdirectzxsem"
                element={
                  <ProtectedRoute>
                    <SubDirectZxsem />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </LocalizationProvider>
  )
}

export default App
